import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Hexagon, MousePointerClick, ArrowUpRight, Menu, X } from "lucide-react";
import { fmtCount } from "@/lib/format";

const LINKS = [
  { id: "how-it-works", label: "How it works", testid: "nav-link-how-it-works" },
  { id: "earnings", label: "Earnings", testid: "nav-link-calculator" },
  { id: "security", label: "Security", testid: "nav-link-security" },
  { id: "faq", label: "FAQ", testid: "nav-link-faq" },
  { id: "testimonials", label: "Proof", testid: "nav-link-testimonials" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const Header = ({ clicks, onJoin }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <motion.header
      data-testid="header-navigation"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b border-amber-100"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <button
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 font-display font-extrabold text-base sm:text-lg text-zinc-900 shrink-0"
          data-testid="nav-logo"
        >
          <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber-400 text-zinc-900">
            <Hexagon className="w-5 h-5" strokeWidth={2.5} />
          </span>
          HoneyGain<span className="hidden min-[420px]:inline text-amber-600">Deals</span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToId(l.id)}
              className="text-sm font-medium text-zinc-600 hover:text-amber-600 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="text-sm font-medium text-zinc-600 hover:text-amber-600 transition-colors"
          >
            Blog
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <span
            data-testid="header-click-counter-badge"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-800"
          >
            <MousePointerClick className="w-3.5 h-3.5" />
            {fmtCount(clicks)} joined via this link
          </span>
          <button
            data-testid="header-affiliate-cta-button"
            onClick={() => onJoin("header")}
            className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 text-white px-3.5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm font-semibold hover:bg-amber-500 hover:text-zinc-900 transition-colors whitespace-nowrap"
          >
            Claim $3 Bonus
            <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            className="lg:hidden grid place-items-center w-9 h-9 rounded-xl border border-amber-200 bg-white text-zinc-900"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-amber-100"
          >
            <div className="px-5 py-4 flex flex-col">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToId(l.id);
                  }}
                  className="text-left py-3.5 px-2 rounded-xl font-display font-bold text-zinc-900 hover:bg-amber-50 transition-colors border-b border-zinc-50"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/blog"
                data-testid="mobile-nav-link-blog"
                onClick={() => setMenuOpen(false)}
                className="py-3.5 px-2 rounded-xl font-display font-bold text-zinc-900 hover:bg-amber-50 transition-colors"
              >
                Blog
              </Link>
              <span
                data-testid="mobile-menu-click-counter"
                className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-800"
              >
                <MousePointerClick className="w-3.5 h-3.5" />
                {fmtCount(clicks)} joined via this link
              </span>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
